import React, { useEffect, useState, useMemo } from 'react'
import * as API from "../../framework/API/api"
import { ENTITYNAME, LOGEVENT, MODULE, PROGRAMSCHEDULESLOTTYPE, SLOT_TYPE, TOAST_TYPES } from "../../framework/constant/constant";
import { useTranslation } from '../../locale/useTranslation';
import { utility } from '../../framework/utility/utilityProvider';
import { EditPageHeader } from '../../components/EditPageHeader';
import BossDialog from '../../components/BossDialog';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { DatePicker } from '@progress/kendo-react-dateinputs';
import ToastNotification from '../../components/ToastNotification';
import { TimePickerWithFormat } from '../../framework/forms/helpercomponents/timepicker/TimePickerWithFormat';
import AddRoundButton from '../../framework/forms/helpercomponents/buttons/AddRoundButton';
import RoundButton from '../../framework/forms/helpercomponents/buttons/RoundButton';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import ProgramScheduleSlotEditForm from '../Planning/ProgramScheduleSlotEditForm';
import CreateRecursiveSlotPopup from '../Planning/PlanningWithSlot/CreateRecursiveSlotPopup';
import moment from 'moment';
import { DateTimeCell } from '../../framework/forms/helpercomponents/CustomGridCells/DateCell';

const SlotEditForm = (props) => {
    const lang = useTranslation();

    const blankDataItem = {
        ...props?.item,
        Title: props?.item?.copy ? "Copy of " + props?.item?.Title : props?.item?.Title ?? "",
        CustomTitle: props?.item?.CustomTitle ?? "",
        Description: props?.item?.Description ?? "",
        CustomDescription: props?.item?.CustomDescription ?? "",
        Code: props?.item?.Code ?? "",
        Duration: props?.item?.Duration ? utility.convertMilisecondsToStringWithFrames(props?.item?.Duration) : "00:00:00:00",
        Genre: props?.item?.Genre ?? {},
        ProductionYear: props?.item?.ProductionYear ?? "",
        PGRating: props?.item?.PGRating ?? {},
        TargetAudience: props?.item?.TargetAudience ?? {},
        ProductionType: props?.item?.ProductionType ?? {},
        SlotType: props?.item?.SlotType ?? {},
        BgColor: props?.item?.BgColor ?? "",
        Breaks: props?.item?.Breaks ?? 0,
        Archive: props?.item?.Archive ?? false,
    }

    const [dataItem, setDataItem] = useState(blankDataItem);
    const [genres, setGenres] = useState([]);
    const [pGRatings, setPGRatings] = useState([]);
    const [targetAudiences, setTargetAudiences] = useState([]);
    const [productionTypes, setProductionTypes] = useState([]);
    const [slotTypes, setSlotTypes] = useState([]);
    const [gridData, setGridData] = useState([]);
    const [channels, setChannels] = useState([]);
    const [filterChannel, setFilterChannel] = useState(null);
    const [filterFromDate, setFilterFromDate] = useState(null);
    const [filterToDate, setFilterToDate] = useState(null);
    const [showProgramScheduleSlotForm, setShowProgramScheduleSlotForm] = useState(false);
    const [selectedProgramScheduleSlotItem, setSelectedProgramScheduleSlotItem] = useState(null);
    const [showRecursiveSlotPopup, setShowRecursiveSlotPopup] = useState(false);
    const [showFilterDialog, setShowFilterDialog] = useState(false);
    const [tempFilterChannel, setTempFilterChannel] = useState(null);
    const [tempFilterFromDate, setTempFilterFromDate] = useState(null);
    const [tempFilterToDate, setTempFilterToDate] = useState(null);

    const allChannelsOption = useMemo(() => ({ _id: "", FullChannelName: "All Channels" }), []);
    const channelOptions = useMemo(() => [allChannelsOption, ...(channels || [])], [allChannelsOption, channels]);

    const isFilterActive = useMemo(() => {
        return Boolean(filterChannel && (filterChannel._id || filterChannel.SID)) || Boolean(filterFromDate) || Boolean(filterToDate);
    }, [filterChannel, filterFromDate, filterToDate]);

    const handleOpenFilterDialog = () => {
        setTempFilterChannel(filterChannel || allChannelsOption);
        setTempFilterFromDate(filterFromDate || null);
        setTempFilterToDate(filterToDate || null);
        setShowFilterDialog(true);
    };

    const handleApplyFilter = () => {
        setFilterChannel(tempFilterChannel && (tempFilterChannel._id || tempFilterChannel.SID) ? tempFilterChannel : null);
        setFilterFromDate(tempFilterFromDate || null);
        setFilterToDate(tempFilterToDate || null);
        setShowFilterDialog(false);
    };

    const handleFilterButtonClick = () => {
        if (isFilterActive) {
            setFilterChannel(null);
            setFilterFromDate(null);
            setFilterToDate(null);
        } else {
            handleOpenFilterDialog();
        }
    };

    useEffect(() => {
        loadCombo();
        if (props?.item?._id) {
            loadProgramScheduleSlots();
        }
    }, [])

    const loadCombo = async () => {
        const genreRes = await API.getDataLookup(ENTITYNAME.Genre, { sort: { Description: 1 } });
        setGenres(genreRes.data);
        const pGRatingRes = await API.getDataLookup(ENTITYNAME.PGRating, { sort: { Description: 1 } });
        setPGRatings(pGRatingRes.data);
        const targetAudienceRes = await API.getDataLookup(ENTITYNAME.Audience, { sort: { Description: 1 } });
        setTargetAudiences(targetAudienceRes.data);
        const productionTypeRes = await API.getDataLookup(ENTITYNAME.ProductionType, { sort: { Description: 1 } });
        setProductionTypes(productionTypeRes.data);
        const channelRes = await API.getDataLookup(ENTITYNAME.Channel, { sort: { FullChannelName: 1 } });
        if (channelRes?.data) {
            setChannels(channelRes.data);
        }
        const slotTypeRes = Object.keys(SLOT_TYPE).map((x) => {
            return {
                _id: SLOT_TYPE[x],
                Description: x
            }
        });
        setSlotTypes(slotTypeRes);
    }

    const loadProgramScheduleSlots = async () => {
        try {
            const res = await API.getData(ENTITYNAME.ProgramScheduleSlot, {query: [['Slot._id', '=', props?.item?._id]], sort: { SID: 1 }});
            if (res.success) {setGridData(res.data ?? []);}
        } catch (err) {
            console.error("Failed to load ProgramScheduleSlots", err);
        }
    };

    const filteredGridData = useMemo(() => {
        if (!gridData || gridData.length === 0) return [];
        return gridData.filter(item => {
            if (filterChannel && (filterChannel._id || filterChannel.SID)) {
                const itemChannelId = item.Channel?._id || item.Channel?.SID;
                const matchChannel = (filterChannel._id && (filterChannel._id === itemChannelId || filterChannel._id === item.Channel?._id)) ||
                                       (filterChannel.SID != null && (filterChannel.SID === itemChannelId || filterChannel.SID === item.Channel?.SID));
                if (!matchChannel) return false;
            }

            if (filterFromDate) {
                const itemTime = typeof item.SlotDateTime === 'number'
                    ? item.SlotDateTime
                    : new Date(item.SlotDateTime).getTime();
                const fromTime = new Date(filterFromDate).setHours(0, 0, 0, 0);
                if (itemTime < fromTime) return false;
            }

            if (filterToDate) {
                const itemTime = typeof item.SlotDateTime === 'number'
                    ? item.SlotDateTime
                    : new Date(item.SlotDateTime).getTime();
                const toTime = new Date(filterToDate).setHours(23, 59, 59, 999);
                if (itemTime > toTime) return false;
            }

            return true;
        });
    }, [gridData, filterChannel, filterFromDate, filterToDate]);
    const onChange = (e) => {
        if (e.target.name === 'Archive') {
            setDataItem({ ...dataItem, Archive: !dataItem.Archive });
        }
        else {
            setDataItem({ ...dataItem, [e.target.name]: e.target.value });
        }
    }
    const isValid = () => {
        if (dataItem.Title == undefined || dataItem.Title == "") {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: lang.please_enter_title_error_message });
            return false;
        }
        return true;
    }

    const handleSubmit = async () => {
        if (!isValid()) return;
        const saveData = {
            _id: dataItem?._id,
            SID: dataItem?.SID,
            Title: dataItem?.Title,
            CustomTitle: dataItem?.CustomTitle,
            Description: dataItem?.Description,
            CustomDescription: dataItem?.CustomDescription,
            Code: dataItem?.Code,
            Duration: dataItem?.Duration ? utility.convertStringWithFramesToMilliseconds(dataItem?.Duration) : null,
            Genre: dataItem?.Genre && Object.keys(dataItem?.Genre).length > 0 ? {
                _id: dataItem?.Genre?._id,
                SID: dataItem?.Genre?.SID,
                Description: dataItem?.Genre?.Description
            } : {},
            ProductionYear: dataItem?.ProductionYear,
            PGRating: dataItem?.PGRating && Object.keys(dataItem?.PGRating).length > 0 ? {
                _id: dataItem?.PGRating?._id,
                Description: dataItem?.PGRating?.Description
            } : {},
            TargetAudience: dataItem?.TargetAudience && Object.keys(dataItem?.TargetAudience).length > 0 ? {
                _id: dataItem?.TargetAudience?._id,
                Description: dataItem?.TargetAudience?.Description
            } : {},
            ProductionType: dataItem?.ProductionType && Object.keys(dataItem?.ProductionType).length > 0 ? {
                _id: dataItem?.ProductionType?._id,
                Description: dataItem?.ProductionType?.Description
            } : {},
            SlotType: dataItem?.SlotType && Object.keys(dataItem?.SlotType).length > 0 ? {
                _id: dataItem?.SlotType?._id,
                Description: dataItem?.SlotType?.Description
            } : {},
            BgColor: dataItem?.BgColor,
            Breaks: dataItem?.Breaks ?? 0,
            Archive: dataItem.Archive ?? false,
            checkDuplicate: true,
            query: [["Title", "=", dataItem.Title]]
        }
        console.log(saveData);
        const res = await API.saveData(ENTITYNAME.Slot, saveData);
        if (res.success) {
            let logData = { event: res.message == "updated" ? LOGEVENT.UPDATE : LOGEVENT.INSERT, module: MODULE.SLOT, data: res.data, message: res.message };
            API.SaveLogs(logData);
            props.cancelEdit();
            if (props.refresh) props.refresh(res.data);
            utility.deleteLocalStorageItem(ENTITYNAME.Slot);
        } else {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: res.message });
        }
    }

    const handleAddProgramScheduleSlot = () => {
        setSelectedProgramScheduleSlotItem({
            _id: undefined,
            SID: 0,
            Channel: {},
            Slot: dataItem,
            Title: dataItem?.Title ?? "",
            CustomTitle: dataItem?.CustomTitle ?? "",
            Description: dataItem?.Description ?? "",
            CustomDescription: dataItem?.CustomDescription ?? "",
            Code: dataItem?.Code ?? "",
            Duration: dataItem?.Duration ?? "00:00:00:00",
            Genre: dataItem?.Genre ?? {},
            ProductionYear: dataItem?.ProductionYear ?? "",
            PGRating: dataItem?.PGRating ?? {},
            TargetAudience: dataItem?.TargetAudience ?? {},
            ProductionType: dataItem?.ProductionType ?? {},
            SlotType: dataItem?.SlotType ?? {},
            ProgramScheduleSlotType: PROGRAMSCHEDULESLOTTYPE?.Primary,
            BgColor: dataItem?.BgColor || "#4559bf",
            Breaks: dataItem?.Breaks ?? 0,
            SlotDateTime: moment.utc().format('YYYY-MM-DDTHH:mm'),
            Remarks: "",
            Archive: false,
        });
        setShowProgramScheduleSlotForm(true);
    };

    const handleEditProgramScheduleSlot = (item) => {
        const fullSlot = (item?.Slot && (item.Slot.Title || Object.keys(item.Slot).length > 2)) ? item.Slot : dataItem;
        setSelectedProgramScheduleSlotItem({
            ...item,
            Slot: fullSlot
        });
        setShowProgramScheduleSlotForm(true);
    };

    const handleProgramScheduleSlotClose = (saved) => {
        setShowProgramScheduleSlotForm(false);
        setSelectedProgramScheduleSlotItem(null);
        if (saved) {loadProgramScheduleSlots();}
    };
    const EditCommandCell = (cellProps) => (
        <td style={{ textAlign: 'center' }}>
            <button className="btn btn-default btn-circle btn-my" title={lang.edit_button_tooltip ?? "Edit"} style={{ margin: '0 2px' }} onClick={() => handleEditProgramScheduleSlot(cellProps.dataItem)}>
                <i className="fa fa-pencil fa-fw" />
            </button>
        </td>
    );

    const TitleCell = (cellProps) => {
        const title = cellProps.dataItem?.Slot?.Title || cellProps.dataItem?.Title || "";
        return <td>{title}</td>;
    };

    const SlotDateCell = (cellProps) => {
        const val = cellProps.dataItem?.SlotDateTime;
        const formatted = val ? moment(val).utc().format("ll") : "";
        return <td>{formatted}</td>;
    };

    const SlotTimeCell = (cellProps) => {
        const val = cellProps.dataItem?.SlotDateTime;
        const formatted = val ? moment(val).utc().format("HH:mm:ss") : "";
        return <td>{formatted}</td>;
    };

    const DurationCell = (cellProps) => {
        const item = cellProps.dataItem;
        const rawDur = item?.Duration ?? item?.Slot?.Duration;
        let formatted = "";
        if (rawDur != null && rawDur !== "") {
            if (typeof rawDur === 'string' && rawDur.includes(':')) {
                formatted = rawDur;
            } else {
                formatted = utility.convertMilisecondsToStringWithFrames(Number(rawDur) || 0);
            }
        } else if (dataItem?.Duration) {
            formatted = dataItem.Duration;
        } else {
            formatted = "00:00:00:00";
        }
        return <td>{formatted}</td>;
    };

    return (
        <>
            <BossDialog
                title={props.item.Title ?? lang.slot_dialog_header}
                onClose={props.cancelEdit}
                width={"820px"}
            >
                <EditPageHeader title={""} onSubmit={handleSubmit} onCancel={props.cancelEdit} showTitle={false} />
                <div className="row g-2 mt-1">
                    {/* Group Box 1: Basic Details */}
                    <div className="col-12">
                        <fieldset className="border rounded" style={{ borderColor: "#dee2e6", padding: "2px 10px 6px 10px", margin: 0 }}>
                            <legend className="float-none w-auto text-muted small px-1 mb-0" style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                Basic Details
                            </legend>
                            <div className="row g-2">
                                <div className="col-4">
                                    <label style={{ fontSize: "12px", marginBottom: "2px" }}>{lang.title_label} *</label>
                                    <input name="Title" type="text" className="form-control form-control-sm" onChange={onChange} value={dataItem.Title} />
                                </div>
                                <div className="col-4">
                                    <label style={{ fontSize: "12px", marginBottom: "2px" }}>{lang.custom_title}</label>
                                    <input name="CustomTitle" type="text" className="form-control form-control-sm" onChange={onChange} value={dataItem.CustomTitle} />
                                </div>
                                <div className="col-4">
                                    <label style={{ fontSize: "12px", marginBottom: "2px" }}>{lang.duration_label}</label>
                                    <TimePickerWithFormat className="form-control form-control-sm" name="Duration" value={dataItem.Duration} onChange={onChange}/>
                                </div>
                                <div className="col-4">
                                    <label style={{ fontSize: "12px", marginBottom: "2px" }}>{lang.description_label} *</label>
                                    <input name="Description" type="text" className="form-control form-control-sm" onChange={onChange} value={dataItem.Description} />
                                </div>
                                <div className="col-4">
                                    <label style={{ fontSize: "12px", marginBottom: "2px" }}>{lang.custom_description_label}</label>
                                    <input name="CustomDescription" type="text" className="form-control form-control-sm" onChange={onChange} value={dataItem.CustomDescription} />
                                </div>
                                <div className="col-4">
                                    <label style={{ fontSize: "12px", marginBottom: "2px" }}>{lang.code_label}</label>
                                    <input name="Code" type="text" className="form-control form-control-sm" onChange={onChange} value={dataItem.Code} />
                                </div>
                            </div>
                        </fieldset>
                    </div>

                    {/* Group Box 2: Metadata */}
                    <div className="col-12">
                        <fieldset className="border rounded" style={{ borderColor: "#dee2e6", padding: "2px 10px 6px 10px", margin: 0 }}>
                            <legend className="float-none w-auto text-muted small px-1 mb-0" style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                Metadata
                            </legend>
                            <div className="row g-2">
                                <div className="col-4">
                                    <label style={{ fontSize: "12px", marginBottom: "2px" }}>{lang.genre_label}</label>
                                    <DropDownList
                                        style={{ backgroundColor: "white" }}
                                        data={genres}
                                        name="Genre"
                                        textField="Description"
                                        dataItemKey="_id"
                                        value={dataItem.Genre}
                                        onChange={onChange}
                                    />
                                </div>
                                <div className="col-4">
                                    <label style={{ fontSize: "12px", marginBottom: "2px" }}>{lang.production_year_label}</label>
                                    <input name="ProductionYear" type="text" className="form-control form-control-sm" onChange={onChange} value={dataItem.ProductionYear} />
                                </div>
                                <div className="col-4">
                                    <label style={{ fontSize: "12px", marginBottom: "2px" }}>{lang.pg_rating_label}</label>
                                    <DropDownList
                                        style={{ backgroundColor: "white" }}
                                        data={pGRatings}
                                        name="PGRating"
                                        textField="Description"
                                        dataItemKey="_id"
                                        value={dataItem.PGRating}
                                        onChange={onChange}
                                    />
                                </div>
                                <div className="col-4">
                                    <label style={{ fontSize: "12px", marginBottom: "2px" }}>{lang.target_audience_label}</label>
                                    <DropDownList
                                        style={{ backgroundColor: "white" }}
                                        data={targetAudiences}
                                        name="TargetAudience"
                                        textField="Description"
                                        dataItemKey="_id"
                                        value={dataItem.TargetAudience}
                                        onChange={onChange}
                                    />
                                </div>
                                <div className="col-4">
                                    <label style={{ fontSize: "12px", marginBottom: "2px" }}>{lang.production_type_label}</label>
                                    <DropDownList
                                        style={{ backgroundColor: "white" }}
                                        data={productionTypes}
                                        name="ProductionType"
                                        textField="Description"
                                        dataItemKey="_id"
                                        value={dataItem.ProductionType}
                                        onChange={onChange}
                                    />
                                </div>
                                <div className="col-4">
                                    <label style={{ fontSize: "12px", marginBottom: "2px" }}>{lang.slot_type_label}</label>
                                    <DropDownList
                                        style={{ backgroundColor: "white" }}
                                        data={slotTypes}
                                        name="SlotType"
                                        textField="Description"
                                        dataItemKey="_id"
                                        value={dataItem.SlotType}
                                        onChange={onChange}
                                    />
                                </div>
                            </div>
                        </fieldset>
                    </div>

                    {/* Group Box 3: Settings & Appearance */}
                    <div className="col-12">
                        <fieldset className="border rounded" style={{ borderColor: "#dee2e6", padding: "2px 10px 6px 10px", margin: 0 }}>
                            <legend className="float-none w-auto text-muted small px-1 mb-0" style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                Settings & Appearance
                            </legend>
                            <div className="row g-2 align-items-center">
                                <div className="col-4">
                                    <label style={{ fontSize: "12px", marginBottom: "2px", display: "block" }}>{lang.background_color_label}</label>
                                    <input
                                        type="color"
                                        name="BgColor"
                                        style={{ width: "45px", height: "26px", padding: "2px", cursor: "pointer" }}
                                        onChange={onChange}
                                        value={dataItem.BgColor || "#4559bf"}
                                        className="form-control form-control-sm"
                                    />
                                </div>
                                <div className="col-4">
                                    <label style={{ fontSize: "12px", marginBottom: "2px" }}>{lang.breaks_label}</label>
                                    <input type="number" className="form-control form-control-sm" name="Breaks" value={dataItem.Breaks} onChange={onChange} />
                                </div>
                                <div className="col-4" style={{ paddingTop: "14px" }}>
                                    <label style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", fontSize: "12px", marginBottom: 0 }}>
                                        <input
                                            style={{ border: "1px solid grey", width: "15px", height: "15px", marginRight: "6px" }}
                                            name={"Archive"}
                                            type="checkbox"
                                            onChange={onChange}
                                            checked={dataItem.Archive}
                                        />
                                        {lang.archive}
                                    </label>
                                </div>
                            </div>
                        </fieldset>
                    </div>

                    {/* Program Schedule Slot Subgrid (when editing existing Slot) */}
                    {props?.item?._id && (
                        <div className="col-12" style={{ marginTop: "2px" }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px', flexWrap: 'wrap', gap: '6px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <AddRoundButton title={lang.add_button_tooltip} onClick={handleAddProgramScheduleSlot}/>
                                    <RoundButton
                                        title="Create Recursive Slot"
                                        icon="repeat"
                                        onClick={() => setShowRecursiveSlotPopup(true)}
                                    />
                                    <label style={{ marginBottom: 0, fontWeight: 600, fontSize: "12px" }}>{lang.program_schedule_slot_label}</label>
                                    <span style={{ fontSize: "11px", color: isFilterActive ? "#2563eb" : "#64748b", fontWeight: isFilterActive ? 600 : 400 }}>
                                        ({filteredGridData.length}{filteredGridData.length !== gridData.length ? ` of ${gridData.length}` : ''})
                                    </span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <RoundButton
                                        title={isFilterActive ? (lang.remove_filter_tooltip || "Filter active (click to remove filter)") : (lang.filter_tooltip || "Filter Program Schedule Slots")}
                                        icon="filter"
                                        btnColor={isFilterActive ? "primary" : "default"}
                                        iconColor={isFilterActive ? "#ffffff" : undefined}
                                        style={isFilterActive ? { backgroundColor: "#3b82f6", color: "#ffffff", borderColor: "#2563eb", margin: "0px 2px 2px 2px" } : { margin: "0px 2px 2px 2px" }}
                                        onClick={handleFilterButtonClick}
                                    />
                                </div>
                            </div>
                            <Grid data={filteredGridData} style={{ maxHeight: '180px', height: '18vh' }} resizable={true}>
                                <GridColumn width="50px" cell={EditCommandCell} resizable={false}/>
                                <GridColumn field="Channel.FullChannelName" title={lang.channel_column || "Channel"} width="150px"/>
                                <GridColumn field="Slot.Title" title={lang.title_column || "Title"} cell={TitleCell} width="150px"/>
                                <GridColumn field="SlotDateTime" title={lang.slot_date_column || "Slot Date"} cell={SlotDateCell} width="125px"/>
                                <GridColumn field="SlotDateTime" title={lang.slot_time_column || "Slot Time"} cell={SlotTimeCell} width="105px"/>
                                <GridColumn title={lang.duration_column || "Duration"} cell={DurationCell} width="115px"/>
                                <GridColumn field="Remarks" title={lang.remark_column || "Remark"}/>
                            </Grid>
                        </div>
                    )}
                </div>
            </BossDialog>
            {showProgramScheduleSlotForm && selectedProgramScheduleSlotItem && (
                <ProgramScheduleSlotEditForm
                    item={selectedProgramScheduleSlotItem}
                    cancelEdit={handleProgramScheduleSlotClose}
                    onClose={handleProgramScheduleSlotClose}
                    refresh={loadProgramScheduleSlots}
                />
            )}
            {showRecursiveSlotPopup && (
                <CreateRecursiveSlotPopup
                    slot={dataItem}
                    onClose={() => setShowRecursiveSlotPopup(false)}
                    onSuccess={() => {
                        setShowRecursiveSlotPopup(false);
                        loadProgramScheduleSlots();
                    }}
                />
            )}
            {showFilterDialog && (
                <BossDialog
                    showHeader={false}
                    zIndex={105}
                    width={"440px"}
                    onClose={() => setShowFilterDialog(false)}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                        <span style={{ fontWeight: 600, fontSize: '13px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <i className="fa fa-filter" style={{ color: '#3b82f6' }}></i>
                            {lang.filter_dialog_header || "Filter Program Schedule Slots"}
                        </span>
                        <button
                            type="button"
                            className="btn btn-sm text-muted p-0"
                            style={{ fontSize: '18px', lineHeight: 1, border: 'none', background: 'none', cursor: 'pointer' }}
                            onClick={() => setShowFilterDialog(false)}
                            title={lang.close_button_tooltip || "Close"}
                        >
                            &times;
                        </button>
                    </div>

                    <div className="form-group mb-3">
                        <label style={{ fontSize: "12px", fontWeight: 600, marginBottom: "4px", display: "block" }}>{lang.channel_label || "Channel"}</label>
                        <DropDownList
                            data={channelOptions}
                            textField="FullChannelName"
                            dataItemKey="_id"
                            value={tempFilterChannel || allChannelsOption}
                            onChange={(e) => setTempFilterChannel(e.target.value)}
                            size="small"
                            style={{ fontSize: "12px", backgroundColor: "#fff", width: "100%" }}
                            popupSettings={{ style: { zIndex: 100002 } }}
                        />
                    </div>

                    <div className="row g-2 mb-3">
                        <div className="col-6">
                            <label style={{ fontSize: "12px", fontWeight: 600, marginBottom: "4px", display: "block" }}>{lang.from_date_label || "From Date"}</label>
                            <DatePicker
                                value={tempFilterFromDate}
                                onChange={(e) => setTempFilterFromDate(e.value)}
                                format="dd/MM/yyyy"
                                placeholder="From Date"
                                size="small"
                                style={{ fontSize: "12px", backgroundColor: "#fff", width: "100%" }}
                                popupSettings={{ style: { zIndex: 100002 } }}
                            />
                        </div>
                        <div className="col-6">
                            <label style={{ fontSize: "12px", fontWeight: 600, marginBottom: "4px", display: "block" }}>{lang.to_date_label || "To Date"}</label>
                            <DatePicker
                                value={tempFilterToDate}
                                onChange={(e) => setTempFilterToDate(e.value)}
                                format="dd/MM/yyyy"
                                placeholder="To Date"
                                size="small"
                                style={{ fontSize: "12px", backgroundColor: "#fff", width: "100%" }}
                                popupSettings={{ style: { zIndex: 100002 } }}
                            />
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '10px', borderTop: '1px solid #e2e8f0' }}>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => {
                                setTempFilterChannel(allChannelsOption);
                                setTempFilterFromDate(null);
                                setTempFilterToDate(null);
                            }}
                            style={{ fontSize: "12px", padding: "4px 12px" }}
                        >
                            {lang.clear_button_text || "Clear"}
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => setShowFilterDialog(false)}
                            style={{ fontSize: "12px", padding: "4px 12px" }}
                        >
                            {lang.cancel_button_text || "Cancel"}
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={handleApplyFilter}
                            style={{ fontSize: "12px", padding: "4px 16px", fontWeight: 600 }}
                        >
                            <i className="fa fa-search" style={{ marginRight: "5px" }}></i>
                            {lang.filter_button_text || lang.search_button_text || "Filter"}
                        </button>
                    </div>
                </BossDialog>
            )}
        </>
    )
}

export default SlotEditForm
